import { useEffect, useState, useCallback, useMemo } from 'react'
import { ChevronLeft, ChevronRight, Search, Trash2, Plus, Loader2, ExternalLink, X } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { pipelineProductsApi } from '@/api/client'
import type {
  PipelineProductMeeting,
  PipelineProductAgendaItem,
  ScopeSearchResult,
  CustomWorkSearchResult,
} from '@/api/client'
import { showToast } from '@/store/uiStore'
import { KYC_RULESETS, V1_KYC_RULESETS } from '@/features/scope/types'

// ───────────────────────── Date helpers ─────────────────────────
function mondayOf(date: Date): Date {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const day = d.getUTCDay()
  const diff = day === 0 ? -6 : 1 - day
  d.setUTCDate(d.getUTCDate() + diff)
  return d
}
function toISODate(date: Date): string {
  return date.toISOString().slice(0, 10)
}
function addDays(date: Date, days: number): Date {
  const d = new Date(date)
  d.setUTCDate(d.getUTCDate() + days)
  return d
}
function formatWeekRange(monday: string): string {
  const m = new Date(monday + 'T00:00:00Z')
  const f = addDays(m, 4) // Friday
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
  const year = m.getUTCFullYear()
  const currentYear = new Date().getUTCFullYear()
  const y = year === currentYear ? '' : `, ${year}`
  return `${m.toLocaleDateString('en-US', opts)} – ${f.toLocaleDateString('en-US', opts)}${y}`
}

// ───────────────────────── Main component ─────────────────────────
export function PipelineProductsTool() {
  const [weekStart, setWeekStart] = useState(() => toISODate(mondayOf(new Date())))
  const [meeting, setMeeting] = useState<PipelineProductMeeting | null>(null)
  const [loading, setLoading] = useState(false)
  const [notesDraft, setNotesDraft] = useState('')
  const [showScopeSearch, setShowScopeSearch] = useState(false)
  const [showCustomWorkSearch, setShowCustomWorkSearch] = useState(false)

  const loadMeeting = useCallback(async (week: string) => {
    setLoading(true)
    const res = await pipelineProductsApi.getMeetingByWeek(week)
    if (res.success && res.data) {
      setMeeting(res.data)
      setNotesDraft(res.data.general_notes || '')
    } else {
      showToast(res.error || 'Failed to load meeting', 'error')
    }
    setLoading(false)
  }, [])

  useEffect(() => { loadMeeting(weekStart) }, [weekStart, loadMeeting])

  const shiftWeek = (deltaDays: number) => {
    const d = new Date(weekStart + 'T00:00:00Z')
    d.setUTCDate(d.getUTCDate() + deltaDays)
    setWeekStart(toISODate(mondayOf(d)))
  }

  const saveNotes = async () => {
    if (!meeting) return
    const res = await pipelineProductsApi.updateMeetingNotes(meeting.id, notesDraft)
    if (!res.success) showToast(res.error || 'Failed to save notes', 'error')
  }

  const handleAddScope = async (scopeId: number) => {
    if (!meeting) return
    const res = await pipelineProductsApi.addAgendaItem(meeting.id, { item_type: 'scope', scope_id: scopeId })
    if (res.success) {
      showToast('Added to agenda', 'success', 1500)
      setShowScopeSearch(false)
      loadMeeting(weekStart)
    } else {
      showToast(res.error || 'Failed to add', 'error')
    }
  }

  const handleAddCustomWork = async (scopeId: number, customWorkId: number) => {
    if (!meeting) return
    const res = await pipelineProductsApi.addAgendaItem(meeting.id, {
      item_type: 'custom_work', scope_id: scopeId, custom_work_id: customWorkId,
    })
    if (res.success) {
      showToast('Added to agenda', 'success', 1500)
      setShowCustomWorkSearch(false)
      loadMeeting(weekStart)
    } else {
      showToast(res.error || 'Failed to add', 'error')
    }
  }

  const handleDeleteItem = async (id: number) => {
    const res = await pipelineProductsApi.deleteItem(id)
    if (res.success) loadMeeting(weekStart)
    else showToast(res.error || 'Failed to delete', 'error')
  }

  const handleUpdateItemNotes = async (id: number, notes: string) => {
    await pipelineProductsApi.updateItem(id, { notes })
  }

  const handleUpdateItemFlag = async (id: number, flag: 'discussed' | 'confirmed_standard', value: boolean) => {
    const res = await pipelineProductsApi.updateItem(id, { [flag]: value })
    if (res.success) {
      // Optimistically refresh so the checkbox reflects and the scope-action status updates reflect elsewhere
      loadMeeting(weekStart)
    } else {
      showToast(res.error || 'Failed to update', 'error')
    }
  }

  const scopeItems = useMemo(() => meeting?.items.filter(i => i.item_type === 'scope') || [], [meeting])
  const cwItems = useMemo(() => meeting?.items.filter(i => i.item_type === 'custom_work') || [], [meeting])

  return (
    <div className="space-y-4">
      {/* Header with week navigator */}
      <Card className="p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">Pipelines and Products</h2>
            <p className="text-xs text-[var(--text-muted)] mt-0.5">
              Weekly agenda with sales &amp; products — upcoming scopes and custom work to discuss.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => shiftWeek(-7)}
              className="p-1.5 rounded border border-[var(--border-color)] hover:bg-[var(--bg-tertiary)] text-[var(--text-secondary)]">
              <ChevronLeft size={16} />
            </button>
            <div className="px-3 py-1 rounded bg-[var(--bg-tertiary)] text-xs font-medium text-[var(--text-primary)] min-w-[180px] text-center">
              Week of {formatWeekRange(weekStart)}
            </div>
            <button onClick={() => shiftWeek(7)}
              className="p-1.5 rounded border border-[var(--border-color)] hover:bg-[var(--bg-tertiary)] text-[var(--text-secondary)]">
              <ChevronRight size={16} />
            </button>
            <button onClick={() => setWeekStart(toISODate(mondayOf(new Date())))}
              className="px-2 py-1 rounded border border-[var(--border-color)] hover:bg-[var(--bg-tertiary)] text-xs text-[var(--text-secondary)]">
              This week
            </button>
          </div>
        </div>

        {/* General notes */}
        <div className="mt-4">
          <label className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">General meeting notes</label>
          <textarea
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            onBlur={saveNotes}
            rows={2}
            placeholder="Anything else for the call..."
            className="mt-1 w-full px-2 py-1.5 rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-sm text-[var(--text-primary)]"
          />
        </div>
      </Card>

      {loading && !meeting && (
        <div className="flex items-center justify-center py-12 text-[var(--text-muted)]">
          <Loader2 size={20} className="animate-spin" />
        </div>
      )}

      {/* Scopes section */}
      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">Upcoming scopes</h3>
            <p className="text-[10px] text-[var(--text-muted)] mt-0.5">Discuss and confirm each scope is standard.</p>
          </div>
          <button onClick={() => setShowScopeSearch(true)}
            className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-[var(--accent-color)]/10 text-[var(--accent-color)] border border-[var(--accent-color)]/30 hover:bg-[var(--accent-color)]/20">
            <Plus size={13} /> Add scope
          </button>
        </div>
        {scopeItems.length === 0 ? (
          <div className="py-6 text-center text-xs text-[var(--text-muted)]">No scopes on the agenda yet.</div>
        ) : (
          <div className="space-y-2">
            {scopeItems.map((item) => (
              <ScopeAgendaRow key={item.id} item={item}
                onDelete={() => handleDeleteItem(item.id)}
                onSaveNotes={(notes) => handleUpdateItemNotes(item.id, notes)}
                onToggleFlag={(flag, v) => handleUpdateItemFlag(item.id, flag, v)} />
            ))}
          </div>
        )}
      </Card>

      {/* Custom work section */}
      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">Custom work</h3>
            <p className="text-[10px] text-[var(--text-muted)] mt-0.5">Pull in specific custom-work items from any scope to discuss.</p>
          </div>
          <button onClick={() => setShowCustomWorkSearch(true)}
            className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-purple-500/10 text-purple-400 border border-purple-500/30 hover:bg-purple-500/20">
            <Plus size={13} /> Add custom work
          </button>
        </div>
        {cwItems.length === 0 ? (
          <div className="py-6 text-center text-xs text-[var(--text-muted)]">No custom work on the agenda yet.</div>
        ) : (
          <div className="space-y-2">
            {cwItems.map((item) => (
              <AgendaItemRow key={item.id} item={item}
                onDelete={() => handleDeleteItem(item.id)}
                onSaveNotes={(notes) => handleUpdateItemNotes(item.id, notes)} />
            ))}
          </div>
        )}
      </Card>

      {showScopeSearch && <ScopeSearchModal onClose={() => setShowScopeSearch(false)} onSelect={handleAddScope} />}
      {showCustomWorkSearch && <CustomWorkSearchModal onClose={() => setShowCustomWorkSearch(false)} onSelect={handleAddCustomWork} />}
    </div>
  )
}

// ───────────────────────── Scope TLDR helpers ─────────────────────────

function safeParse<T = any>(raw: string | null | undefined): T | null {
  if (!raw) return null
  try { return JSON.parse(raw) as T } catch { return null }
}

function yesNo(v: any): boolean | null {
  if (v === true || v === 'yes' || v === 'true') return true
  if (v === false || v === 'no' || v === 'false') return false
  return null
}

// Pretty vendor names (case-insensitive lookup, falls back to title-case of the raw value).
const VENDOR_DISPLAY: Record<string, string> = {
  comply: 'ComplyAdvantage',
  complyadvantage: 'ComplyAdvantage',
  daon: 'Daon',
  refinitiv: 'Refinitiv',
  dowjones: 'Dow Jones',
  acuris: 'Acuris',
  socure: 'Socure',
  gdc: 'GDC',
  datazoo: 'Data Zoo',
  'data zoo': 'Data Zoo',
  equifax: 'Equifax',
  experian: 'Experian',
  frankieone: 'FrankieOne',
}
function prettyVendor(v: any): string {
  if (!v || typeof v !== 'string') return '—'
  const key = v.toLowerCase().trim()
  if (VENDOR_DISPLAY[key]) return VENDOR_DISPLAY[key]
  if (key === 'unknown' || key === '') return '—'
  // Title-case fallback
  return v.replace(/\b\w/g, (c) => c.toUpperCase())
}

// Map flow-level IdvProvider enum to a display name (returns '' for unknown/empty).
function prettyFlowVendor(v: any): string {
  if (!v || typeof v !== 'string') return ''
  switch (v.toLowerCase()) {
    case 'daon': return 'Daon'
    case 'incode': return 'Incode'
    case 'no_vendor': return 'No vendor'
    case 'unknown':
    case '':
      return ''
    default: return v.replace(/\b\w/g, (c: string) => c.toUpperCase())
  }
}

// Format an ISO date string ("YYYY-MM-DD" or full ISO) as "8 May 2026".
function prettyDate(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso.length <= 10 ? iso + 'T00:00:00Z' : iso)
  if (isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

// Look up the human-readable label for a KYC ruleset key (V2 first, V1 fallback).
function rulesetLabel(key: string): string {
  if (!key) return ''
  const v2 = KYC_RULESETS.find((r) => r.key === key)
  if (v2) return v2.label
  const v1 = V1_KYC_RULESETS.find((r) => r.key === key)
  if (v1) return v1.label
  return key
}

// Map biometrics frontend enum to a human label (Hosted vs Embedded).
function frontendLabel(frontend: string): string {
  switch ((frontend || '').toLowerCase()) {
    case 'hosted_onesdk': return 'Hosted OneSDK'
    case 'embedded_onesdk': return 'Embedded OneSDK'
    case 'embedded': return 'Embedded'
    case 'none': return 'No frontend'
    case 'other': return 'Other'
    case 'unknown':
    case '':
      return ''
    default: return frontend
  }
}

// Map the structured integrationType enum to a human label.
function integrationLabel(intType: string): string {
  switch ((intType || '').toLowerCase()) {
    case 'api': return 'API only'
    case 'portal': return 'Portal only'
    case 'both': return 'API + Portal'
    case 'unknown':
    case '':
      return 'Not specified'
    default: return intType
  }
}

type TldrRow = {
  label: string
  /** Colour of the label column (maps to Tailwind text classes) */
  tone: 'integration' | 'usecase' | 'dates' | 'volume' | 'kyc' | 'idv' | 'aml' | 'risk' | 'fraud' | 'kyb'
  /** "on" = subject is enabled, "off" = explicitly disabled, "unknown" = dimmed */
  state: 'on' | 'off' | 'unknown'
  /** Each entry renders on its own line in the value column */
  lines: string[]
}

function buildScopeTLDR(item: PipelineProductAgendaItem): TldrRow[] {
  const rows: TldrRow[] = []
  const gi = safeParse<any>(item.general_info)
  const kyc = safeParse<any>(item.kyc_config)
  const aml = safeParse<any>(item.aml_config)
  const bio = safeParse<any>(item.biometrics_config)
  const kyb = safeParse<any>(item.kyb_config)
  const rc = safeParse<any>(item.rules_config)

  // ── Integration ──
  {
    const intType = (gi?.integrationType || '').toString()
    const platformV = (gi?.platformVersion || '').toString().toUpperCase() // V1 / V2
    const portalUsers = Array.isArray(gi?.portalAccessUsers) ? gi.portalAccessUsers.length : 0
    let headline = integrationLabel(intType)
    if ((intType === '' || intType.toLowerCase() === 'unknown') && portalUsers > 0) {
      headline = `Portal users (${portalUsers}) — type not set`
    }
    const lines = [headline]
    if (platformV) lines.push(`Platform: ${platformV}`)
    const state: TldrRow['state'] = (intType === '' || intType.toLowerCase() === 'unknown') && portalUsers === 0 ? 'unknown' : 'on'
    rows.push({ label: 'Integration', tone: 'integration', state, lines })
  }

  // ── Client Use Case ──
  {
    const useCase = (gi?.clientUseCase || '').trim()
    rows.push({
      label: 'Use case',
      tone: 'usecase',
      state: useCase ? 'on' : 'unknown',
      lines: [useCase || 'Not specified'],
    })
  }

  // ── Dates ──
  {
    const go = gi?.goLiveDate || ''
    const hard = !!gi?.isHardGoLiveDate
    const prod = gi?.productionAccessDate || ''
    const lines: string[] = []
    if (go) lines.push(`Go-live: ${prettyDate(go)}${hard ? ' (HARD DATE)' : ''}`)
    if (prod) lines.push(`Prod access: ${prettyDate(prod)}`)
    rows.push({
      label: 'Key dates',
      tone: 'dates',
      state: lines.length === 0 ? 'unknown' : 'on',
      lines: lines.length === 0 ? ['Not specified'] : lines,
    })
  }

  // ── Volume ──
  {
    const forecast = gi?.forecastChecks12m
    const hasVolume = forecast != null && forecast !== '' && forecast !== 0
    const lines: string[] = []
    if (hasVolume) {
      const n = Number(forecast)
      const formatted = !isNaN(n) ? n.toLocaleString('en-AU') : String(forecast)
      lines.push(`${formatted} checks (12 months)`)
    }
    rows.push({
      label: 'Volume',
      tone: 'volume',
      state: hasVolume ? 'on' : 'unknown',
      lines: hasVolume ? lines : ['Not specified'],
    })
  }

  // ── KYC ──
  {
    const kycReq = yesNo(kyc?.kycRequired)
    if (kycReq === false) {
      rows.push({ label: 'KYC', tone: 'kyc', state: 'off', lines: ['Disabled'] })
    } else if (kycReq === true) {
      const rulesetKeys: string[] = Array.isArray(kyc?.rulesets)
        ? kyc.rulesets.map((r: any) => r?.rulesetKey || r?.key).filter(Boolean)
        : []
      const rulesetLabels = rulesetKeys.map(rulesetLabel)
      const jurisdictions: string[] = Array.isArray(kyc?.primaryCustomerJurisdictions) ? kyc.primaryCustomerJurisdictions : []
      const hasInternational = jurisdictions.some((j: string) => j && !/^australia$/i.test(j))
        || (Array.isArray(kyc?.internationalCountries) && kyc.internationalCountries.some((c: string) => c && c !== 'None'))
      const intlCountries: string[] = Array.isArray(kyc?.internationalCountries)
        ? kyc.internationalCountries.filter((c: string) => c && c !== 'None')
        : []
      const scopeTag = hasInternational ? 'International' : (jurisdictions.some((j: string) => /^australia$/i.test(j)) ? 'AU only' : 'Enabled')
      const lines: string[] = [scopeTag]
      if (rulesetLabels.length > 0) {
        // Each ruleset on its own line
        for (const rl of rulesetLabels) lines.push(`• ${rl}`)
      } else if (jurisdictions.length > 0) {
        lines.push(`Jurisdictions: ${jurisdictions.join(', ')}`)
      }
      if (intlCountries.length > 0) {
        lines.push(`Countries: ${intlCountries.join(', ')}`)
      }
      rows.push({ label: 'KYC', tone: 'kyc', state: 'on', lines })
    } else {
      rows.push({ label: 'KYC', tone: 'kyc', state: 'unknown', lines: ['—'] })
    }
  }

  // ── IDV / Biometrics ──
  {
    const idvEnabled = yesNo(bio?.enabled)
    if (idvEnabled === false) {
      rows.push({ label: 'IDV', tone: 'idv', state: 'off', lines: ['Disabled'] })
    } else if (idvEnabled === true) {
      const vendor = prettyVendor(bio?.vendor)
      const frontend = frontendLabel(bio?.frontend || '')
      const brandingNotes = (bio?.brandingNotes || '').toString().trim()
      const daonNotes = (bio?.daonEngagementNotes || '').toString().trim()
      const lines: string[] = []
      // Primary vendor + frontend
      lines.push(frontend ? `Vendor: ${vendor}  ·  ${frontend}` : `Vendor: ${vendor}`)

      // Flow-level details
      const flows: Array<any> = Array.isArray(bio?.flows) ? bio.flows : []
      for (const flow of flows) {
        const flowName = (flow.name || '').trim()
        const flowVendor = prettyFlowVendor(flow.vendor)
        const caps: string[] = []
        if (yesNo(flow.idFormEnabled) === true) caps.push('ID Form')
        if (yesNo(flow.idVerificationEnabled) === true) caps.push('ID Verification')
        if (yesNo(flow.biometricsEnabled) === true) caps.push('Biometrics')
        if (yesNo(flow.doubleIdEnabled) === true) caps.push('Double ID')
        if (yesNo(flow.reviewScreenEnabled) === true) caps.push('Review Screen')
        const label = flowName || (flow.isDefault ? 'Default flow' : 'Flow')
        const parts = [label]
        if (flowVendor) parts.push(`(${flowVendor})`)
        if (caps.length > 0) parts.push(`— ${caps.join(', ')}`)
        lines.push(`• ${parts.join(' ')}`)
      }

      // Branding requirements
      const brandingFlags: string[] = []
      if (yesNo(bio?.rebrandingHostedOneSDK) === true) brandingFlags.push('OneSDK rebrand')
      if (yesNo(bio?.rebrandingDaon) === true) brandingFlags.push('Daon rebrand')
      if (yesNo(bio?.extendedBranding) === true) brandingFlags.push('Extended branding')
      if (yesNo(bio?.customRedirectPage) === true) brandingFlags.push('Custom redirect')
      if (yesNo(bio?.customConsents) === true) brandingFlags.push('Custom consents')
      if (brandingFlags.length > 0) lines.push(`Branding: ${brandingFlags.join(', ')}`)
      if (brandingNotes) lines.push(`Branding notes: ${brandingNotes}`)
      if (daonNotes) lines.push(`Daon engagement: ${daonNotes}`)
      rows.push({ label: 'IDV', tone: 'idv', state: 'on', lines })
    } else {
      rows.push({ label: 'IDV', tone: 'idv', state: 'unknown', lines: ['—'] })
    }
  }

  // ── AML ──
  {
    const amlLevel = (aml?.level || '').toString()
    const amlEnabled = amlLevel && amlLevel !== 'na' && amlLevel !== 'unknown'
    if (amlEnabled) {
      const vendor = prettyVendor(aml?.vendor)
      // Adverse media is on if either (a) the level is 'enhanced' (implies all screens)
      // or (b) the explicit flag is 'yes' or (c) any adverse media categories / comply IDs are set.
      const adverseFlag = yesNo(aml?.adverseMediaScreeningConfig?.enabled)
      const adverseCategories = Array.isArray(aml?.adverseMediaScreeningConfig?.categories) ? aml.adverseMediaScreeningConfig.categories.length : 0
      const adverseCustom = (aml?.adverseMediaScreeningConfig?.customCategories || '').toString().trim()
      const complyIds = Array.isArray(aml?.adverseMediaScreeningConfig?.complyAdvanceMediaIds) ? aml.adverseMediaScreeningConfig.complyAdvanceMediaIds.length : 0
      const adverseEnabled = adverseFlag === true
        || amlLevel === 'enhanced'
        || adverseCategories > 0
        || !!adverseCustom
        || complyIds > 0
      const screening = adverseEnabled ? 'PEP + Sanctions + Adverse Media' : 'PEP + Sanctions'
      rows.push({ label: 'AML', tone: 'aml', state: 'on', lines: [screening, `Vendor: ${vendor}`] })
    } else if (amlLevel === 'na') {
      rows.push({ label: 'AML', tone: 'aml', state: 'off', lines: ['Not applicable'] })
    } else {
      rows.push({ label: 'AML', tone: 'aml', state: 'unknown', lines: ['—'] })
    }
  }

  // ── Risk (notes only) ──
  {
    const riskNotes = (rc?.riskConfig?.notes || '').trim()
    rows.push({
      label: 'Risk',
      tone: 'risk',
      state: riskNotes ? 'on' : 'unknown',
      lines: [riskNotes || 'No notes'],
    })
  }

  // ── Fraud (separate row) ──
  {
    const fraudEnabled = yesNo(rc?.rulesConfig?.fraudEnabled)
    if (fraudEnabled === true) {
      const fraudTypeLabels: Record<string, string> = {
        device_intelligence: 'Device Intelligence',
        behavioural_biometrics: 'Behavioural Biometrics',
        phone_risk: 'Phone Risk Assessment',
        email_risk: 'Email Risk Assessment',
        transaction_monitoring: 'Transaction & Activity Monitoring',
      }
      const fraudTypes = rc?.rulesConfig?.fraudTypes || []
      const lines = fraudTypes.length > 0
        ? fraudTypes.map((t: string) => fraudTypeLabels[t] || t)
        : ['Enabled']
      rows.push({ label: 'Fraud', tone: 'fraud', state: 'on', lines })
    } else if (fraudEnabled === false) {
      rows.push({ label: 'Fraud', tone: 'fraud', state: 'off', lines: ['Disabled'] })
    } else {
      rows.push({ label: 'Fraud', tone: 'fraud', state: 'unknown', lines: ['—'] })
    }
  }

  // ── KYB ──
  {
    const kybEnabled = yesNo(kyb?.enabled)
    if (kybEnabled === false) {
      rows.push({ label: 'KYB', tone: 'kyb', state: 'off', lines: ['Disabled'] })
    } else if (kybEnabled === true) {
      const intl = yesNo(kyb?.internationalEnabled) === true
      const trust = yesNo(kyb?.trustAnalyserEnabled) === true
      const intlCountryProducts: Array<{ country: string; products: string[] }> = Array.isArray(kyb?.internationalCountryProducts) ? kyb.internationalCountryProducts : []
      const lines: string[] = [intl ? 'International' : 'Domestic']
      if (trust) lines.push('Trust Analyser')
      if (intl && intlCountryProducts.length > 0) {
        const countryNames = intlCountryProducts.map((cp: { country: string }) => cp.country).filter(Boolean)
        if (countryNames.length > 0) {
          lines.push(`Countries: ${countryNames.join(', ')}`)
        }
      }
      rows.push({ label: 'KYB', tone: 'kyb', state: 'on', lines })
    } else {
      rows.push({ label: 'KYB', tone: 'kyb', state: 'unknown', lines: ['—'] })
    }
  }

  return rows
}

const TONE_CLASSES: Record<TldrRow['tone'], string> = {
  integration: 'text-sky-400',
  usecase: 'text-indigo-400',
  dates: 'text-amber-400',
  volume: 'text-teal-400',
  kyc: 'text-emerald-400',
  idv: 'text-violet-400',
  aml: 'text-rose-400',
  risk: 'text-orange-400',
  fraud: 'text-red-400',
  kyb: 'text-cyan-400',
}

// ───────────────────────── Scope agenda row ─────────────────────────
function ScopeAgendaRow({
  item, onDelete, onSaveNotes, onToggleFlag,
}: {
  item: PipelineProductAgendaItem
  onDelete: () => void
  onSaveNotes: (notes: string) => void
  onToggleFlag: (flag: 'discussed' | 'confirmed_standard', value: boolean) => void
}) {
  const [notes, setNotes] = useState(item.notes || '')
  useEffect(() => { setNotes(item.notes || '') }, [item.notes])
  const scopeUrl = item.deal_id ? `/scope?session=${item.deal_id}` : null
  const tldr = useMemo(() => buildScopeTLDR(item), [item])
  const isUpsell = useMemo(() => {
    const gi = safeParse<any>(item.general_info)
    return gi?.isUpsell === true
  }, [item.general_info])

  return (
    <div className="rounded border border-[var(--border-color)] bg-[var(--bg-tertiary)]/30 p-3">
      {/* Header row: deal name + link + flags + delete */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-[var(--text-primary)] truncate" title={item.deal_name}>
            {item.deal_name}
          </span>
          <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
            isUpsell
              ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
              : 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
          }`}>
            {isUpsell ? 'Upsell' : 'New'}
          </span>
          {scopeUrl && (
            <a href={scopeUrl} target="_blank" rel="noreferrer"
              className="text-[var(--text-muted)] hover:text-[var(--accent-color)]" title="Open scope">
              <ExternalLink size={13} />
            </a>
          )}
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)] cursor-pointer select-none">
            <input type="checkbox" className="accent-[var(--accent-color)]"
              checked={item.discussed} onChange={(e) => onToggleFlag('discussed', e.target.checked)} />
            Discussed
          </label>
          <label className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)] cursor-pointer select-none">
            <input type="checkbox" className="accent-emerald-500"
              checked={item.confirmed_standard} onChange={(e) => onToggleFlag('confirmed_standard', e.target.checked)} />
            Standard / OOB
          </label>
          <button onClick={onDelete} className="text-[var(--text-muted)] hover:text-red-400" title="Remove">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* TLDR — 2-column label/value grid (each row renders its value lines stacked) */}
      {tldr.length > 0 && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 rounded bg-[var(--bg-primary)]/40 border border-[var(--border-color)] p-3">
          {tldr.map((row, i) => {
            const dimmed = row.state === 'unknown' || row.state === 'off'
            return (
              <div key={i} className="flex items-start gap-3 text-xs leading-snug">
                <span className={`${TONE_CLASSES[row.tone]} font-semibold uppercase tracking-wide text-[10px] shrink-0 w-28 pt-0.5`}>
                  {row.label}
                </span>
                <div className={`${dimmed ? 'text-[var(--text-muted)]' : 'text-[var(--text-primary)]'} flex-1 min-w-0 space-y-0.5`}>
                  {row.lines.map((line, j) => (
                    <div key={j} className="break-words">{line}</div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        onBlur={() => onSaveNotes(notes)}
        rows={2}
        placeholder="Notes from the discussion..."
        className="mt-2 w-full px-2 py-1.5 rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-xs text-[var(--text-primary)]"
      />
    </div>
  )
}

// ───────────────────────── Custom work row ─────────────────────────
function AgendaItemRow({
  item, onDelete, onSaveNotes,
}: {
  item: PipelineProductAgendaItem
  onDelete: () => void
  onSaveNotes: (notes: string) => void
}) {
  const [notes, setNotes] = useState(item.notes || '')
  useEffect(() => { setNotes(item.notes || '') }, [item.notes])

  const scopeUrl = item.deal_id ? `/scope?session=${item.deal_id}` : null

  return (
    <div className="rounded border border-[var(--border-color)] bg-[var(--bg-tertiary)]/30 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[var(--text-primary)] truncate" title={item.deal_name}>
              {item.deal_name}
            </span>
            {scopeUrl && (
              <a href={scopeUrl} target="_blank" rel="noreferrer"
                className="text-[var(--text-muted)] hover:text-[var(--accent-color)]" title="Open scope">
                <ExternalLink size={12} />
              </a>
            )}
          </div>
          {item.item_type === 'custom_work' && (
            <div className="mt-0.5 text-xs text-purple-400">
              {item.custom_work_name || '(untitled)'}
              {item.custom_work_category && <span className="ml-1.5 text-[10px] text-[var(--text-muted)]">· {item.custom_work_category}</span>}
              {item.custom_work_due_date && <span className="ml-1.5 text-[10px] text-[var(--text-muted)]">· due {item.custom_work_due_date}</span>}
              {item.custom_work_effort_days != null && <span className="ml-1.5 text-[10px] text-[var(--text-muted)]">· {item.custom_work_effort_days}d</span>}
            </div>
          )}
          {item.custom_work_description && (
            <div className="mt-1 text-[11px] text-[var(--text-secondary)] line-clamp-2">{item.custom_work_description}</div>
          )}
        </div>
        <button onClick={onDelete} className="text-[var(--text-muted)] hover:text-red-400 shrink-0" title="Remove">
          <Trash2 size={14} />
        </button>
      </div>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        onBlur={() => onSaveNotes(notes)}
        rows={2}
        placeholder="Notes from the discussion..."
        className="mt-2 w-full px-2 py-1 rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-xs text-[var(--text-primary)]"
      />
    </div>
  )
}

// ───────────────────────── Search modals ─────────────────────────
function ScopeSearchModal({ onClose, onSelect }: { onClose: () => void; onSelect: (scopeId: number) => void }) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState<ScopeSearchResult[]>([])
  const [searching, setSearching] = useState(false)

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return }
    const timer = setTimeout(async () => {
      setSearching(true)
      const res = await pipelineProductsApi.searchScopes(q.trim())
      if (res.success && res.data) setResults(res.data.results)
      setSearching(false)
    }, 250)
    return () => clearTimeout(timer)
  }, [q])

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-16 z-50" onClick={onClose}>
      <div className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg w-full max-w-lg p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">Add scope to agenda</h3>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X size={16} /></button>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-2.5 text-[var(--text-muted)]" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by deal name or company..."
            className="w-full pl-8 pr-3 py-1.5 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] text-sm text-[var(--text-primary)]" />
        </div>
        <div className="max-h-96 overflow-y-auto">
          {searching && <div className="py-6 text-center text-xs text-[var(--text-muted)]"><Loader2 size={14} className="animate-spin inline" /></div>}
          {!searching && q.trim().length >= 2 && results.length === 0 && (
            <div className="py-6 text-center text-xs text-[var(--text-muted)]">No scopes found.</div>
          )}
          {results.map((r) => (
            <button key={r.scope_id} onClick={() => onSelect(r.scope_id)}
              className="w-full text-left p-2 rounded hover:bg-[var(--bg-tertiary)] flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[var(--text-primary)] truncate">{r.deal_name}</div>
                {r.customer_name && r.customer_name !== r.deal_name && (
                  <div className="text-[10px] text-[var(--text-muted)] truncate">{r.customer_name}</div>
                )}
              </div>
              <Plus size={14} className="text-[var(--text-muted)] shrink-0 mt-0.5" />
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

function CustomWorkSearchModal({
  onClose, onSelect,
}: {
  onClose: () => void
  onSelect: (scopeId: number, customWorkId: number) => void
}) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState<CustomWorkSearchResult[]>([])
  const [searching, setSearching] = useState(false)

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return }
    const timer = setTimeout(async () => {
      setSearching(true)
      const res = await pipelineProductsApi.searchCustomWorks(q.trim())
      if (res.success && res.data) setResults(res.data.results)
      setSearching(false)
    }, 300)
    return () => clearTimeout(timer)
  }, [q])

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-16 z-50" onClick={onClose}>
      <div className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg w-full max-w-xl p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">Add custom work to agenda</h3>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X size={16} /></button>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-2.5 text-[var(--text-muted)]" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search custom work name, description, or deal..."
            className="w-full pl-8 pr-3 py-1.5 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] text-sm text-[var(--text-primary)]" />
        </div>
        <div className="max-h-96 overflow-y-auto space-y-1">
          {searching && <div className="py-6 text-center text-xs text-[var(--text-muted)]"><Loader2 size={14} className="animate-spin inline" /></div>}
          {!searching && q.trim().length >= 2 && results.length === 0 && (
            <div className="py-6 text-center text-xs text-[var(--text-muted)]">No custom work found.</div>
          )}
          {results.map((r) => (
            <button key={`${r.scope_id}-${r.custom_work_id}`} onClick={() => onSelect(r.scope_id, r.custom_work_id)}
              className="w-full text-left p-2 rounded hover:bg-[var(--bg-tertiary)] flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-purple-400 truncate">{r.name || '(untitled)'}</div>
                <div className="text-[10px] text-[var(--text-muted)] truncate">{r.deal_name}</div>
                {r.description && <div className="text-[10px] text-[var(--text-secondary)] line-clamp-1 mt-0.5">{r.description}</div>}
                <div className="text-[9px] text-[var(--text-muted)] mt-0.5">
                  {r.category && <span>{r.category}</span>}
                  {r.due_date && <span className="ml-1.5">· due {r.due_date}</span>}
                  {r.effort_days != null && <span className="ml-1.5">· {r.effort_days}d</span>}
                  {r.lifecycle_status && <span className="ml-1.5">· {r.lifecycle_status}</span>}
                </div>
              </div>
              <Plus size={14} className="text-[var(--text-muted)] shrink-0 mt-0.5" />
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
